













































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { lessonWebservice } from '@/webservices/lesson';
import type { Trade } from '@/models/trade';
import type { Topic } from '@/models/topic';
import type { User } from '@/models/user';
import type { Lesson } from '@/models/lesson';
import type { Phase } from '@/models/phase';
import type { Tag } from '@/models/tag';
import TradesList from '@/components/knowledge/trades/TradesList.vue';
import SearchField from '@/components/prine/SearchField.vue';
import { hasAnyPermission, hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';
import TagSelect from '@/components/form/TagSelect.vue';

@Component({
    name: 'TradesListGroupedByPhases',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasAnyPermission, hasPermission },
    components: {
        TagSelect,
        TradesList,
        SearchField,
    },
})
export default class TradesListGroupedByPhases extends Vue {
    private trades = new Array<Trade>();
    private tradesByPhase = new Map<number, Trade[]>();
    // private originalTrades = new Array<Trade>();
    private topics = new Array<Topic>();
    private lessonsCount: number = 0;
    private personalLessonsCount: number = 0;
    private isSearching: boolean = false;
    @Getter('knowledge/tradeSearchString')
    private searchQuery: string;
    private filterByLessons: boolean = hasPermission(PERMISSIONS.APPEX_LESSON);
    // private phases = new Array<Phase>();
    private isLoaded: boolean = false;
    private isLoading: boolean = false;
    private tableSettings = {
        openedDetailed: Array<number>(),
        isStriped: true,
        isHoverable: true,
        isPaginated: false,
    };

    private selectedPhase: Phase = {} as Phase;

    get selectedTag(): Tag | null {
        return this.$store.getters['knowledge/tradeSelectedTag'];
    }

    set selectedTag(value: Tag | null) {
        this.$store.commit('knowledge/setTradeSelectedTag', value);
    }

    @Prop() private phases: Phase[];
    @Prop() private originalTrades: Trade[];

    @Getter('account/userInfo') private userInfo: User;

    @Watch('originalTrades')
    private onOriginalTradesChange() {
        this.prepareData();
    }

    @Watch('phases')
    private onPhasesChange() {
        this.prepareData();
    }

    @Watch('searchQuery')
    private onSearchQueryValueChange() {
        this.search();
    }

    @Watch('selectedTag')
    private onSelectedTagChange() {
        this.search();
    }

    get activePhaseTabIndex() {
        return this.$store.getters['knowledge/tradesActivePhaseTabIndex'];
    }

    // noinspection JSUnusedGlobalSymbols
    set activePhaseTabIndex(index: number) {
        this.isLoading = true;
        this.$store.commit('knowledge/setTradesActivePhaseTabIndex', index);
        setTimeout(() => {
            this.isLoading = false;
        }, 200);
    }

    private mounted() {
        this.prepareData();
        this.getLessonsCount();
        this.getPersonalLessonsCount();
    }

    private phaseTabTitle(phase: Phase, index: number) {
        if (this.activePhaseTabIndex === index) {
            return phase.abbreviation + ': ' + phase.title;
        } else {
            return phase.abbreviation;
        }
    }

    private groupTradesByPhase() {
        // Initialize the trades per phase map
        this.tradesByPhase = new Map<number, Trade[]>();
        this.phases.forEach((phase) => {
            const filteredTrades = [] as Trade[];
            this.trades.forEach((trade: Trade) => {
                trade.lessons_count = trade.lessons.length;
                const filteredLessons = trade.lessons.filter((lesson: Lesson) => {
                    return lesson.phases.findIndex((ob) => ob.id === phase.id) !== -1 || phase.id === 0;
                });
                if (filteredLessons.length > 0 || this.searchQuery.length === 0) {
                    const tradeCopy: Trade = JSON.parse(JSON.stringify(trade));
                    tradeCopy.lessons = trade.lessons;
                    tradeCopy.lessons_count = filteredLessons.length;
                    filteredTrades.push(tradeCopy);
                }
            });

            this.tradesByPhase.set(phase.id, filteredTrades);
        });

        // Add all trades in the index 0
        this.tradesByPhase.set(0, this.trades);
    }

    private updateSearchQuery(value: string) {
        this.$store.commit('knowledge/setTradeSearchString', value);
    }

    private search() {
        console.log('SEARCHING');
        this.isSearching = true;

        const regExQuery = new RegExp(this.searchQuery.trim(), 'i');

        let originalTradesCopy: Trade[];
        originalTradesCopy = JSON.parse(JSON.stringify(this.originalTrades));

        this.trades = originalTradesCopy
            .filter((trade: Trade) => {
                const tradeTitleMatch = trade.title.search(regExQuery) !== -1;
                const tradeBKPMatch = trade.bkp.toString().search(regExQuery) !== -1;

                if (this.filterByLessons) {
                    const filteredLessons = trade.lessons.filter((lesson: Lesson) => {
                        if (lesson.more_info) {
                            return (
                                lesson.insight.search(regExQuery) !== -1 || lesson.more_info.search(regExQuery) !== -1
                            );
                        } else {
                            return lesson.insight.search(regExQuery) !== -1;
                        }
                    });
                    trade.lessons = filteredLessons;
                    trade.lessons_count = filteredLessons.length;

                    return tradeBKPMatch || tradeTitleMatch || trade.lessons.length > 0;
                } else {
                    return tradeBKPMatch || tradeTitleMatch;
                }
            })
            .filter((trade) => {
                if (this.selectedTag && this.selectedTag.id !== 0) {
                    trade.lessons = this.filterLessonsByTag(trade.lessons);
                    trade.lessons_count = trade.lessons.length;
                    return trade.lessons.length > 0;
                }
                return true;
            });

        this.groupTradesByPhase();

        this.isSearching = false;
    }

    private filterLessonsByTag(lessons: Lesson[]): Lesson[] {
        const selectedTag = this.selectedTag;
        if (!selectedTag || selectedTag.id === 0) {
            return lessons;
        }

        return lessons.filter((lesson: Lesson) => {
            if (lesson.sub_lessons && lesson.sub_lessons.length > 0) {
                lesson.sub_lessons = this.filterLessonsByTag(lesson.sub_lessons);
            }

            const hasTag = lesson.tags ? lesson.tags.some((tag) => tag.id === selectedTag.id) : false;
            return hasTag || (lesson.sub_lessons && lesson.sub_lessons.length > 0);
        });
    }

    private getLessonsCount() {
        lessonWebservice
            .count()
            .then((response) => {
                this.lessonsCount = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private getPersonalLessonsCount() {
        lessonWebservice
            .countPersonal()
            .then((response) => {
                this.personalLessonsCount = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private prepareData() {
        this.trades = JSON.parse(JSON.stringify(this.originalTrades));
        this.isLoaded = true;
        this.groupTradesByPhase();
        if (this.$route.query.searchQuery && this.$route.query.searchQuery !== '') {
            this.$store.commit(
                'knowledge/setTradeSearchString',
                decodeURIComponent(this.$route.query.searchQuery.toString())
            );
            if (this.$route.query.filterByLessons) {
                this.filterByLessons = this.$route.query.filterByLessons === 'true';
            }
        }
        this.search();
    }

    private rowClicked(row) {
        if (hasPermission(PERMISSIONS.APPEX_LESSON)) {
            this.phaseTabChanged(this.activePhaseTabIndex);
            this.$router.push({
                name: 'GroupedLessonsList',
                query: {
                    tradeId: row.id,
                    searchQuery: encodeURIComponent(this.searchQuery),
                    filterByLessons: this.filterByLessons.toString(),
                    selectedPhaseId: this.selectedPhase.id ? this.selectedPhase.id.toString() : 'all',
                    selectedTagId: this.selectedTag && this.selectedTag.id ? this.selectedTag.id.toString() : 'all',
                },
            });
        }
    }

    private phaseTabChanged(index: number) {
        // getting buefy tabs component by refs
        const buefyTabs: any = this.$refs.phasesTabs;
        this.selectedPhase = {} as Phase;

        this.phases.forEach((phase) => {
            // checking if the current label of active tab item matches the phase
            // if true set the phase and the selectedPhase
            if (
                phase.abbreviation === buefyTabs.items[index].label ||
                phase.abbreviation + ': ' + phase.title === buefyTabs.items[index].label
            ) {
                this.selectedPhase = phase;
            }
        });
    }

    private clearSearch() {
        this.$store.commit('knowledge/clearSearchStrings');
    }
}
